import type { DeliveryAddress } from '@carsfinders/contracts';

/**
 * Bids domain types — self-contained copy for the admin SPA (mirrors the web
 * `Bid` type + the api-node projections). IDs/dates are strings here (api-node
 * serialises Mongo ObjectId / Date to JSON). This admin view always runs as the
 * admin role, so GET /bids returns EVERY user's bids with a populated `user`.
 */

/** Auction-facing bid status (web parity — exact string values). */
export enum BidStatus {
  Active = 'Under Review',
  Outbid = 'Outbid',
  Cancelled = 'Not Won',
  Won = 'Won',
}

/** i18n keys for a bid status badge (reuse the shared `tabs.bids.*` labels). */
export const BID_STATUS_LABEL_KEYS: Record<BidStatus, string> = {
  [BidStatus.Active]: 'tabs.bids.underReview',
  [BidStatus.Outbid]: 'tabs.bids.outbid',
  [BidStatus.Cancelled]: 'tabs.bids.notWon',
  [BidStatus.Won]: 'tabs.bids.won',
};

/** Bid-relay desk pipeline state (Phase 9) — mirrors contracts RelayStatus. */
export type RelayStatus =
  | 'captured'
  | 'queued'
  | 'relayed'
  | 'won'
  | 'lost'
  | 'cancelled'
  | 'expired'
  // Phase 13: the operator tried to place it and the real auction refused.
  | 'upstream_rejected';

/** Why the upstream auction refused a bid (mirror of contracts RelayRejectReason). */
export type RelayRejectReason = 'amount_below_reserve' | 'lot_withdrawn' | 'account_limit' | 'other';

/** The exact vocabulary `POST /admin/relay-queue/:id/reject` validates — the select offers nothing else. */
export const RELAY_REJECT_REASONS: readonly RelayRejectReason[] = [
  'amount_below_reserve',
  'lot_withdrawn',
  'account_limit',
  'other',
];

/**
 * `POST /admin/relay-queue/:id/confirm-relay` body — EXACTLY the contracts
 * ConfirmRelayBodySchema vocabulary (phase-1 lesson: free text 400ed every
 * save). `upstreamPlacedAt` travels as an ISO string; zod coerces it.
 */
export interface ConfirmRelayPayload {
  upstreamRef?: string;
  upstreamAmount?: number;
  upstreamPlacedAt?: string;
  force?: boolean;
  /** Phase 15: what the operator read on the real auction (X) — contracts ConfirmRelayBodySchema. */
  upstreamHigherChf?: number;
  /** Phase 15: the raise within the client's own auto-bid limit — only with `upstreamHigherChf`. */
  raiseToChf?: number;
}

/** `POST /admin/relay-queue/:id/reject` body (contracts RejectRelayBodySchema). */
export interface RejectRelayPayload {
  reason: RelayRejectReason;
  note?: string;
  force?: boolean;
}

/** SLA badge severity of a relay-queue row (contracts SlaSeverity). */
export type SlaSeverity = 'green' | 'amber' | 'red';

/** Populated bidder (admin $lookup projection on GET /bids). */
export interface BidUser {
  _id?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  /**
   * Google avatar URL, mirrors `clients.types.User.photoURL`. NOT yet projected
   * by `BidReadHelper`'s admin `$lookup` (apps/api/src/modules/bids/bid-read.helper.ts) —
   * always undefined today. Field kept so `BidsTable` renders a real photo the
   * moment that projection adds it, with no further frontend change.
   */
  photoURL?: string;
  /**
   * Client phone (`clients.types.User.phone`). Populated only on GET
   * /bids/:id (`BidDetailService.getBidDetail`, apps/api/src/modules/bids/bid-detail.service.ts)
   * — that projection currently reads `{ name, email, phone }`, and the
   * lookup itself matches `_id` against the raw `bid.userId` STRING while
   * `users._id` is an ObjectId, so in practice it resolves to `null` for
   * every real bid today. See the doc comment on `BidDetailResponse.user`.
   */
  phone?: string;
  /** Where the won car goes (`users.deliveryAddress`) — GET /bids/:id, admin only. */
  deliveryAddress?: DeliveryAddress | null;
}

/** One bid row as returned by GET /bids?view=standard for an admin. */
export interface AdminBid {
  _id: string;
  userId: string;
  carId: string;
  bid: number;
  status: BidStatus;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  relayStatus?: RelayStatus;
  tenderCloseAt?: string | null;
  processedOnAuction?: boolean;
  user?: BidUser | null;
  // Phase 13 — admin-only projection fields
  claimedBy?: string | null;
  claimedAt?: string | null;
  upstreamRef?: string | null;
  upstreamAmount?: number | null;
  upstreamPlacedAt?: string | null;
  relayRejectReason?: RelayRejectReason | null;
  relayCancelReason?: string | null;
  /**
   * Lot card photo, resolved server-side by `BidReadHelper` (admin view) via
   * `lotCardImageUrl` — a browser-ready URL, or null when the lot has no
   * usable image. Absent on non-admin rows.
   */
  carThumbnailUrl?: string | null;
  /**
   * The lot's «brand model», its `listing_id` and `data_source`, read from the
   * same `alpicar_cars` $lookup as the thumbnail (admin view only). The last
   * two build the PUBLIC lot URL (BUG-33). Null when the lot is gone.
   */
  carTitle?: string | null;
  carListingId?: string | null;
  carDataSource?: string | null;
  /** The lot's real auction close (ISO, no customer offset); null when unknown. */
  carEndsAt?: string | null;
}

/** Minimal car detail returned alongside a single bid (GET /bids/:id). */
export interface BidDetailCar {
  _id?: string;
  ai_brand?: string;
  ai_model?: string;
  brand?: string;
  model?: string;
  make?: string;
  auction_name?: string;
  listing_id?: string;
  /** Source discriminator (alpicar/autach/…) — the public lot URL's first segment. */
  data_source?: string;
  end_date?: string;
  /** The real close ingest writes (a BSON Date, JSON-serialised to ISO). */
  auction_end_date?: string;
  thumbnail_image?: string;
  detail_images?: string[];
}

/**
 * GET /bids/:id response envelope.
 *
 * `user` is ALWAYS `null` on the live API today — a pre-existing bug, not a
 * business rule: `BidDetailService.getBidDetail` looks it up with
 * `usersCollection.findOne({ _id: bid.userId })` where `bid.userId` is a raw
 * STRING and `users._id` is a Mongo ObjectId, so the native-driver query never
 * matches (confirmed against live dev data 2026-09-22). Render this field
 * defensively — a client without a name/email/phone is the CURRENT reality,
 * not a hypothetical edge case, until that query is fixed server-side.
 */
export interface BidDetailResponse {
  bid: AdminBid & Record<string, unknown>;
  car: BidDetailCar | null;
  /**
   * The lot's card photo, resolved server-side (`lotCardImageUrl`). `car` is
   * the RAW row, whose `thumbnail_image` is empty or a CDN key on a live
   * alpicar lot — never render that; render this. Null when there is no photo.
   */
  carImageUrl?: string | null;
  user: BidUser | null;
  /**
   * Admin-only: the deal this bid became (bidId → deals), with its state —
   * `won` means won but not invoiced yet. Null when the bid never won.
   */
  deal?: { id: string; state: string } | null;
  /**
   * Admin-only (local-ledger bridge): the bidder holds the `admin` role — the
   * owners bidding for themselves. Such a win is booked in the local ledger
   * («Додати до обліку»), never invoiced. Absent from an older API.
   */
  bidderIsAdmin?: boolean;
  /** Admin-only: the ledger card this bid already became, or null. */
  localVehicleId?: string | null;
}

/** One row of GET /admin/relay-queue (contracts RelayQueueItem). */
export interface RelayQueueItem {
  bidId: string;
  carId: string;
  userId: string;
  bid: number;
  maxBid?: number | null;
  relayStatus: RelayStatus;
  tenderCloseAt: string | null;
  slaSeverity: SlaSeverity;
  createdAt?: string | null;
  /** Phase 13 operator claim — admin userId holding the row, or null. */
  claimedBy?: string | null;
  /** MASKED email of the claimer (`j***@x.com`), for the «у роботі» badge. */
  claimedByLabel?: string | null;
  claimedAt?: string | null;
  car: {
    brand?: string | null;
    model?: string | null;
    listing_id?: string | null;
    current_bid?: unknown;
  } | null;
  user: {
    email?: string | null;
    biddingPower?: number | null;
  } | null;
}

/** GET /admin/relay-queue response envelope. */
export interface RelayQueueResponse {
  success: boolean;
  data: RelayQueueItem[];
}

/** Admin relay action verbs (keys of the contracts transition matrix). */
export type RelayAction = 'queue' | 'confirm-relay' | 'outcome' | 'cancel-relay' | 'reject';

/** Phase 13 claim verbs — a work lock, not a relay transition. */
export type RelayClaimAction = 'claim' | 'release';

/**
 * Which actions are legal from a given relay status (mirror of the contracts
 * RELAY_TRANSITIONS matrix — kept local so the admin SPA needs no new dep).
 */
export const RELAY_TRANSITIONS: Record<RelayAction, readonly RelayStatus[]> = {
  queue: ['captured'],
  'confirm-relay': ['captured', 'queued'],
  outcome: ['relayed'],
  'cancel-relay': ['captured', 'queued', 'relayed'],
  // Phase 13: the upstream auction refused the bid.
  reject: ['relayed', 'queued'],
};

/**
 * Actions the API refuses (403 CLAIM_REQUIRED) unless the acting admin holds
 * the row's claim — mirror of contracts RELAY_CLAIM_REQUIRED_ACTIONS.
 */
export const RELAY_CLAIM_REQUIRED_ACTIONS: readonly RelayAction[] = [
  'confirm-relay',
  'outcome',
  'reject',
];

/**
 * Relay states that mean the bid has already been placed on the real auction
 * (or decided there) — the operator's «оброблено» is a fact, not a choice.
 */
export const RELAY_PLACED_STATES: readonly RelayStatus[] = ['relayed', 'won', 'lost'];

/**
 * «Оброблено на аукціоні» as the detail page shows it: the manual flag, OR a
 * state that can only exist after the operator acted — the bid is Won, or its
 * relay reached a placed/decided state. Derived, so the checkbox can never
 * contradict the status badge next to it («Виграно» + «не оброблено»).
 */
export function isProcessedOnAuction(
  bid: Pick<AdminBid, 'processedOnAuction' | 'status' | 'relayStatus'>,
): { processed: boolean; derived: boolean } {
  const derived =
    bid.status === BidStatus.Won ||
    (bid.relayStatus !== undefined && RELAY_PLACED_STATES.includes(bid.relayStatus));
  return { processed: derived || Boolean(bid.processedOnAuction), derived };
}

/**
 * «Оброблено» on this bid means placing it on the real auction (owner,
 * 2026-10-25): an open bid the relay may still confirm (`captured`/`queued`),
 * not yet marked. Marking it runs claim → confirm-relay, the same flow «Угоди»
 * used to offer as «Віддзеркалити»; the server writes the mark with the flip.
 */
export function isMirrorableOnAuction(
  bid: Pick<AdminBid, 'processedOnAuction' | 'status' | 'relayStatus'>,
): boolean {
  return (
    bid.status === BidStatus.Active &&
    !bid.processedOnAuction &&
    RELAY_TRANSITIONS['confirm-relay'].includes(bid.relayStatus ?? 'captured')
  );
}

/**
 * A FINISHED bid — decided: Won or Not Won. Its operator actions (mark won /
 * not won, «оброблено») and the lot's minimum-price control no longer apply,
 * so the detail page hides them. Outbid is NOT finished: the lot is still
 * running and the client may bid again.
 */
export function isFinishedBid(bid: Pick<AdminBid, 'status'>): boolean {
  return bid.status === BidStatus.Won || bid.status === BidStatus.Cancelled;
}

/** Payment standing of a won bid's deal, as the bid page's pill shows it. */
export type DealPaymentStanding = 'uninvoiced' | 'unpaid' | 'paid' | 'forfeited';

/**
 * Deal state → payment standing. `won` has no invoice yet; `invoice_issued` /
 * `deposit_applied` are billed but not settled; `paid` and every rung after it
 * are settled. `voided` (the win was taken away) has no standing — null.
 */
export function dealPaymentStanding(state: string): DealPaymentStanding | null {
  switch (state) {
    case 'won':
      return 'uninvoiced';
    case 'invoice_issued':
    case 'deposit_applied':
      return 'unpaid';
    case 'paid':
    case 'export_docs':
    case 'in_transit':
    case 'customs':
    case 'delivered':
    case 'closed':
      return 'paid';
    case 'forfeited':
      return 'forfeited';
    default:
      return null;
  }
}

/** Relay states in which a row may be claimed (contracts RELAY_CLAIMABLE_STATES). */
export const RELAY_CLAIMABLE_STATES: readonly RelayStatus[] = ['captured', 'queued', 'relayed'];

/** One row of GET /admin/audit (contracts AuditEntry). */
export interface AuditEntry {
  id: string;
  type: string;
  aggregate?: string | null;
  /** ISO-8601 outbox createdAt. */
  at: string;
  /** Actor (admin userId) when the row names one; null for platform-driven rows. */
  who: string | null;
  /** MASKED admin email (`j***@x.com`) for `who`, or the raw id when unresolved. */
  whoLabel?: string | null;
  bidId: string | null;
  carId: string | null;
  userId: string | null;
  data: Record<string, unknown>;
}

/** GET /admin/audit response envelope. */
export interface AuditResponse {
  success: boolean;
  data: AuditEntry[];
  count: number;
  limit: number;
}

/** GET /admin/cars/:carId/minimum-bid — the lot floor and where it came from. */
export interface MinimumBidState {
  /** Effective floor a new bid must beat. */
  minimumBidPrice: number;
  /** The raw value an admin last typed (0 when never set). */
  adminSetMinimum: number;
  /** Highest bid recorded on the lot. */
  highestBidAmount: number;
  source: 'admin' | 'dynamic' | 'initial';
  lastUpdate?: string | null;
}

/** PATCH /admin/cars/:carId/minimum-bid response. */
export interface SetMinimumBidResponse {
  success: boolean;
  minimumBidPrice: number;
  previousMinimum: number;
  /** Active bids demoted to Outbid because they fell below the new floor. */
  outbidBidsCount: number;
  source: 'admin' | 'dynamic' | 'initial';
}

/** POST /admin/cars/:carId/cancel-all-bids response. */
export interface CancelAllBidsResponse {
  success: boolean;
  carId: string;
  cancelledCount: number;
  cancelledBidIds: string[];
}

/**
 * 409 body of PATCH /bids/:id/status when the lot already has a winner — the
 * operator must confirm before the replacement is re-issued with forceReplace.
 */
export interface WinnerReplacementConfirmation {
  requiresConfirmation: true;
  message: string;
  existingWinners: Array<{
    bidId: string;
    userId: string;
    amount: number;
    createdAt?: string;
  }>;
}
